import { Elements } from "@stripe/react-stripe-js"
import { loadStripe } from "@stripe/stripe-js"
import { useCallback, useState } from "react"
import { Button, Form, FormControl, InputGroup, Modal } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import Swal from "sweetalert2"
import { useApi } from "../../../../api"
import { ErrorDialog } from "../../../../components/ErrorDialog"
import { ErrorResponseDialog } from "../../../../components/ErrorResponseDialog"
import { InjectedCheckoutForm } from "../../../../components/InjectedCheckoutForm"
import { SuccessDialog } from "../../../../components/SuccessDialog"
import { ICargo, INftAsset, IOtp } from "../../../../interfaces"

const stripePromise = loadStripe(window.location.hostname === "localhost" ? 'pk_test_70OCDjwDvtq6KIznGEYCB9l8' : 'pk_live_8x88Bq3MeZPoybzdXtNMDMpl');

interface NftDetailPurchaseModalProps {
  asset: INftAsset;
  onClose: () => void;
}
export const NftDetailPurchaseModal = ({ asset, onClose }: NftDetailPurchaseModalProps) => {
  const { t } = useTranslation()
  const projectCode = 'NFT';
  const api = useApi(projectCode);
  const [isLoading, setLoading] = useState(false)
  const [otp, setOtp] = useState<string | null>(null)
  const [otpTag, setOtpTag] = useState<IOtp>()

  const paymentMethods = ['nnego', 'usdt', 'eth'];
  if (asset.ownerProfile.isAgency) {
    paymentMethods.push('creditCard')
  }
  if (asset.ownerProfile.isMerchant) {
    paymentMethods.push('gas')
  }
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null)

  const handleClose = () => {
    onClose()
  };

  const handlePaymentResponse = useCallback((cargo: ICargo) => {
    setLoading(false)
    if (cargo.returnCode === '000000') {
      SuccessDialog({ title: t('success'), cb: () => {
        if (window.localStorage.getItem('orgSrc')) {
          const backUrl = window.localStorage.getItem('orgSrc');
          window.localStorage.removeItem('orgSrc');
          window.localStorage.removeItem('orgTitle');
          window.localStorage.href = backUrl;
        }
      } })
    } else {
      ErrorResponseDialog(cargo, t)
    }
  }, [t])

  return (
    <>
      <Modal
        show={true}
        onHide={handleClose}
        backdrop="static"
        keyboard={false}
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>購買 {asset.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-gray-500 px-4 px-md-5 py-4">
          <div>
            <span>{t('buyingPrice')} : </span>
            <span className="text-rose-500 font-bold">{new Intl.NumberFormat('en').format(asset['sellingPrice'])}</span>
          </div>
          <div className="mt-3">
            <div>{t('paymentMethod')}</div>
            <Form className="ml-3 text-sm">
              {
                paymentMethods.map(method => {
                  return (
                    <Form.Check
                      key={method}
                      type='radio'
                      name='paymentMethod'
                      label={t(method)}
                      onClick={() => {
                        setPaymentMethod(method)
                      }}
                      disabled={method === 'eth'}
                    />
                  )
                })
              }
            </Form>
            {
              (paymentMethod === 'gas' || paymentMethod === 'nnego' || paymentMethod === 'usdt') && <Form.Group className="mt-3">
                <Form.Label>{t('oneTimePasscode')}</Form.Label>
                <InputGroup>
                  <FormControl
                    type="password"
                    onChange={(e) => {
                      setOtp(e.target.value)
                    }}
                    aria-label={t('pleaseInputOtp')}
                  />
                  <Button variant="outline-danger" disabled={isLoading} onClick={() => {
                    setLoading(true)
                    api.sendOtp((cargo: ICargo) => {
                      setLoading(false)
                      if (cargo.returnCode === '000000') {
                        setOtpTag(cargo.info);
                        SuccessDialog({ title: t('otpSentSuccess') })
                      } else {
                        switch (cargo.returnCode) {
                          case '100003':
                            ErrorDialog({ title: t(`errorCode.${cargo.returnCode}`) })
                            window.location.href = `/web/login/${projectCode}`
                            break;
                          default:
                            ErrorDialog({ title: t('otpSentFailure') })
                        }
                      }
                    })
                  }}>{t('sendOtp')}</Button>
                </InputGroup>
                {otpTag && <div className="text-sm">驗證碼編號：{otpTag?.tag}</div>}
              </Form.Group>
            }
            {
              paymentMethod === 'creditCard' && <div className="mt-3">
                <Elements stripe={stripePromise}>
                  <InjectedCheckoutForm projectCode={projectCode} product='nftTrading' nftId={asset['nftId']} amount={asset['sellingPrice'] || 0} onSuccess={() => {
                    handleClose()
                  }} />
                </Elements>
              </div>
            }
          </div>
        </Modal.Body>
        <Modal.Footer className="py-4 px-4 px-md-5">
          {
            paymentMethod !== 'creditCard' && <Button className="bt-buy" disabled={isLoading} onClick={() => {
              if (paymentMethod === null) {
                ErrorDialog({ title: t('pleaseSelectPaymentMethod') })
                return
              }
              if (otp === null) {
                ErrorDialog({ title: t('pleaseInputOtp') })
                return
              }
              if (paymentMethod === 'gas') {
                setLoading(true)
                api.tradingNftByGas(asset['nftId'], otp, handlePaymentResponse)
              }
              if (paymentMethod === 'nnego') {
                setLoading(true)
                api.tradingNftByNnego(asset['nftId'], otp, handlePaymentResponse)
              }
              if (paymentMethod === 'usdt') {
                setLoading(true)
                Swal.fire({
                  title: t('processing'),
                  html: '區塊鏈交易中請等5-10秒',
                  timer: 10 * 1000,
                  timerProgressBar: true,
                  showConfirmButton: false,
                  didOpen: () => {
                    Swal.showLoading(Swal.getDenyButton())
                  },
                  willClose: () => {
    
                  }
                }).then((result) => {
                  /* Read more about handling dismissals below */
                  if (result.dismiss === Swal.DismissReason.timer) {
                    console.log('I was closed by the timer')
                  }
                })
                api.tradingNftByUsdt(asset['nftId'], otp, handlePaymentResponse)
              }
            }}>{isLoading ? t('processing') : t('checkout')}</Button>
          }
        </Modal.Footer>
      </Modal>
    </>
  )
}